import { Link } from "react-router-dom";
import { Layers, ArrowRight, Wallet, Bot } from "lucide-react";
import { ValueTrendChart } from "@/components/ValueTrendChart";

const money = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (v) => `${v >= 0 ? "+" : "-"}${money(Math.abs(v))}`;

const Row = ({ icon: Icon, label, to, value, pnl, note, testid }) => (
  <Link to={to} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-bt-surface-2 border border-bt-border-subtle hover:border-bt-border-focus transition-colors group" data-testid={testid}>
    <span className="flex items-center gap-2 min-w-0">
      <Icon className="w-4 h-4 text-bt-gold shrink-0" strokeWidth={1.5} />
      <span className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
        <span className="font-body text-sm text-bt-text-primary truncate">{label}</span>
        {note && <span className="font-mono text-[10px] text-bt-text-tertiary truncate">{note}</span>}
      </span>
    </span>
    <span className="flex items-center gap-3 shrink-0">
      <span className="text-right">
        <span className="block font-mono text-sm text-bt-text-primary" data-testid={`${testid}-value`}>{money(value)}</span>
        <span className={`block font-mono text-[11px] ${pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{signed(pnl)}</span>
      </span>
      <ArrowRight className="w-3 h-3 text-bt-text-tertiary group-hover:text-bt-gold transition-colors" />
    </span>
  </Link>
);

export const PortfolioSummary = ({ portfolio, woodrow, isAdmin }) => {
  const manualValue = portfolio.reduce((s, p) => s + (p.total_value || 0), 0);
  const manualPnl = portfolio.reduce((s, p) => s + (p.pnl || 0), 0);
  const wValue = woodrow?.portfolio_value || 0;
  const wPnl = woodrow?.total_pnl || 0;
  const showManual = !isAdmin || portfolio.length > 0;
  const total = (showManual ? manualValue : 0) + wValue;
  const totalPnl = (showManual ? manualPnl : 0) + wPnl;

  return (
    <div className="md:col-span-2 bg-bt-surface border border-bt-border-subtle p-6 stagger-2" data-testid="portfolio-summary">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
        <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Total Portfolio Value</h2>
      </div>
      <p className="text-3xl lg:text-4xl font-mono font-medium tracking-tighter text-bt-text-primary" data-testid="portfolio-total-value">
        {money(total)}
      </p>
      <p className={`font-mono text-sm mt-1 ${totalPnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="portfolio-pnl">
        {signed(totalPnl)} Total P&L
      </p>
      <div className="mt-4 space-y-2">
        {showManual && (
          <Row icon={Wallet} label="My Picks" to="/portfolio" value={manualValue} pnl={manualPnl}
            note={portfolio.length ? `${portfolio.length} holding${portfolio.length === 1 ? "" : "s"}` : "no holdings yet"} testid="summary-manual" />
        )}
        {woodrow && (
          <Row icon={Bot} label="Woodrow AI" to="/woodrow" value={wValue} pnl={wPnl}
            note={`${woodrow.total_return >= 0 ? "+" : ""}${woodrow.total_return ?? 0}% return${woodrow.paused ? " · paused" : ""}`} testid="summary-woodrow" />
        )}
      </div>
      <ValueTrendChart live={woodrow ? { woodrow: wValue, portfolio: showManual && portfolio.length ? manualValue : null } : null} />
    </div>
  );
};
